import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import baseURL from "../../../../Url/NodeBaseURL";
import { Col, Row, Button, Dropdown, DropdownButton } from "react-bootstrap";
import InputField from "./../../Transactions/SalesForm/InputfieldSales";
import { FaTrash, FaCamera, FaUpload } from "react-icons/fa";
import Webcam from "react-webcam";
import { useNavigate } from "react-router-dom";
import { AiOutlinePlus } from "react-icons/ai";

const ProductDetails = ({
  handleAdd,
  handleUpdate,
  isEditing,
  isVendor,
  formData,
  data,
  handleChange,
  handleImageChange,
  fileInputRef,
  clearImage,
  captureImage,
  setShowWebcam,
  showWebcam,
  webcamRef,
  setShowOptions,
  showOptions,
  handleBarcodeChange,
  handleProductNameChange,
  handleMetalTypeChange,
  handleDesignNameChange,
  products,
  filteredDesignOptions,
  filteredPurityOptions,
  filteredMetalTypes,
  categoryOptions,
  subcategoryOptions,
  designOptions,
  uniqueProducts,
  purityOptions,
  metaltypeOptions,
  isBarcodeSelected,
  isQtyEditable,
  handleClear,
}) => {
  const navigate = useNavigate();

  const isByFixed = formData.pricing === "By fixed";
  const vendorAssigned = isVendor === true;
  
  // Check if this is a tagged barcode product
  const isTagEntryProduct =
    !!formData.opentag_id &&
    String(formData.opentag_id).trim() !== "" &&
    String(formData.opentag_id).trim().toLowerCase() !== "null";

  const defaultBarcode = formData.category
    ? products.find((product) => product.product_name === formData.category)
        ?.rbarcode || ""
    : "";

  // Generate options list for barcode selection
  const barcodeOptions = [
    ...products.map((product) => ({
      value: product.rbarcode,
      label: product.rbarcode,
    })),
    ...data.map((tag) => ({
      value: tag.PCode_BarCode,
      label: tag.PCode_BarCode,
    })),
  ];

  // Ensure default barcode is included in options
  if (
    defaultBarcode &&
    !barcodeOptions.some((option) => option.value === defaultBarcode)
  ) {
    barcodeOptions.unshift({ value: defaultBarcode, label: defaultBarcode });
  }

  // Set default barcode only if formData.code is empty
  useEffect(() => {
    if (!formData.code && defaultBarcode) {
      handleBarcodeChange(defaultBarcode);
    }
  }, [formData.category, defaultBarcode]);

  const isRunningTag = formData.pricing === "By Running Tag";

  // State to store full subcategory data for "By Running Tag" products
  const [selectedSubcategoryData, setSelectedSubcategoryData] = useState(null);

  // Fetch full subcategory details when product_name changes and pricing is "By Running Tag"
  useEffect(() => {
    const fetchSubcategoryDetails = async () => {
      if (!formData.product_name || formData.pricing !== "By Running Tag") {
        setSelectedSubcategoryData(null);
        return;
      }

      try {
        // Fetch all subcategories to find the one matching product_name
        const response = await axios.get(`${baseURL}/subcategory`);
        const allSubcategories = response.data?.data || [];
        
        const selectedSubCategory = allSubcategories.find(
          (cat) => cat.sub_category_name === formData.product_name
        );

        if (selectedSubCategory && selectedSubCategory.subcategory_id) {
          const detailResponse = await axios.get(
            `${baseURL}/subcategory/${selectedSubCategory.subcategory_id}`
          );
          setSelectedSubcategoryData(detailResponse.data);
        }
      } catch (error) {
        console.error("Error fetching subcategory details:", error);
        setSelectedSubcategoryData(null);
      }
    };

    fetchSubcategoryDetails();
  }, [formData.product_name, formData.pricing]);

  // Calculate selling purity for "By Running Tag" products
  const calculateSellingPurityForRunningTag = useCallback(() => {
    if (formData.pricing !== "By Running Tag") {
      return null;
    }

    const parsePercent = (val) => {
      if (val === null || val === undefined || val === "") return 0;
      const s = val.toString().replace("%", "").trim();
      const n = Number(s);
      return Number.isFinite(n) ? n : 0;
    };

    // Get current tag data if it's a tag entry product
    const currentTag = isTagEntryProduct && formData.code
      ? data.find((tag) => String(tag.PCode_BarCode) === String(formData.code))
      : null;

    // Get purchase purity - prioritize formData (set from barcode scan), then tag data, then subcategory
    let purchasePuritySource = formData.pur_purityPercentage || formData.purchase_manual_purity;
    
    // If formData has pur_Purity, check if it's "Manual"
    if (!purchasePuritySource && formData.pur_Purity) {
      if (String(formData.pur_Purity).toLowerCase().includes("manual")) {
        purchasePuritySource = formData.pur_purityPercentage;
      } else {
        purchasePuritySource = formData.pur_Purity;
      }
    }
    
    // Fallback to tag or subcategory
    if (!purchasePuritySource) {
      purchasePuritySource = currentTag?.pur_purityPercentage || 
        currentTag?.pur_Purity ||
      selectedSubcategoryData?.purchase_manual_purity ||
        "";
    }
    
    const purchasePurity = parsePercent(purchasePuritySource);

    // Get purchase MC% (only if MC On is "MC %")
    let purchaseMC = 0;
    const mcOnValue = formData.pur_Making_Charges_On || formData.mc_on || currentTag?.pur_Making_Charges_On || "";
    
    if (mcOnValue === "MC %" || formData.pur_MC_Per_Gram || currentTag?.pur_MC_Per_Gram) {
      const mcSource = formData.pur_MC_Per_Gram ||
        formData.purchase_mc_percent ||
        currentTag?.pur_MC_Per_Gram ||
        selectedSubcategoryData?.purchase_mc_percent ||
        "";
      purchaseMC = parsePercent(mcSource);
    }

    // Get purchase wastage%
    const purchaseWastageSource = formData.pur_Wastage_Percentage ||
      formData.purchase_wastage_percent ||
      currentTag?.pur_Wastage_Percentage ||
      selectedSubcategoryData?.purchase_wastage_percent ||
      "";
    const purchaseWastage = parsePercent(purchaseWastageSource);

    // Get selling addon purity - check formData, then subcategory
    const sellingAddonPuritySource = formData.selling_addon_purity ||
      selectedSubcategoryData?.selling_addon_purity ||
      "";
    const sellingAddonPurity = parsePercent(sellingAddonPuritySource);

    // Get selling addon MC%
    const sellingAddonMCSource = formData.selling_addon_mc_percent ||
      selectedSubcategoryData?.selling_addon_mc_percent ||
      "";
    const sellingAddonMC = parsePercent(sellingAddonMCSource);

    // Get selling addon wastage%
    const sellingAddonWastageSource = formData.selling_addon_wastage_percent ||
      selectedSubcategoryData?.selling_addon_wastage_percent ||
      "";
    const sellingAddonWastage = parsePercent(sellingAddonWastageSource);

    // Selling Purity = Purchase Purity + Selling Addon Purity only
    // MC and Wastage are shown separately in their own fields (MC % and Wastage %)
    const totalPurity = purchasePurity + sellingAddonPurity;
    
    return totalPurity > 0 ? totalPurity.toFixed(2) : null;
  }, [
    formData.pricing,
    formData.code,
    formData.pur_purityPercentage,
    formData.purchase_manual_purity,
    formData.pur_Purity,
    formData.pur_Making_Charges_On,
    formData.mc_on,
    formData.pur_MC_Per_Gram,
    formData.purchase_mc_percent,
    formData.pur_Wastage_Percentage,
    formData.purchase_wastage_percent,
    formData.selling_addon_purity,
    formData.selling_addon_mc_percent,
    formData.selling_addon_wastage_percent,
    isTagEntryProduct,
    data,
    selectedSubcategoryData,
  ]);


  const handleGenerateOrder = () => {
    // If you want to send the current formData:
    navigate("/sales", { state: { orderDetails: [formData] } });
  };

  return (
    <Col>
      <Row>
        <Col xs={12} md={2}>
          <InputField
            label="BarCode/Rbarcode"
            name="code"
            value={formData.code || defaultBarcode} // Default barcode when formData.code is empty
            onChange={(e) => handleBarcodeChange(e.target.value)}
            type="select"
            options={barcodeOptions}
            disabled={vendorAssigned}
            autoFocus
          />
        </Col>
        <Col xs={12} md={2} className="d-flex align-items-center">
          <div style={{ flex: 1 }}>
            <InputField
              label="Category"
              name="category"
              value={formData.category || ""}
              type="select"
              onChange={handleChange}
              options={categoryOptions}
              disabled={vendorAssigned || isTagEntryProduct}
            />
          </div>
          <AiOutlinePlus
            size={20}
            color="black"
            style={{
              marginLeft: "10px",
              cursor: isTagEntryProduct ? "not-allowed" : "pointer",
              marginBottom: "9px",
              ...(isTagEntryProduct && { opacity: 0.5 }),
            }}
            onClick={() =>
              !isTagEntryProduct && navigate("/itemmaster", {
                state: { orderDetails: [formData], from: "/orders" },
              })
            }
          />
        </Col>
        <Col xs={12} md={2}>
          <InputField
            label="Metal Type"
            name="metal_type"
            value={formData.metal_type || ""}
            onChange={handleChange}
            type="select"
            options={metaltypeOptions}
            disabled={vendorAssigned || isTagEntryProduct}
          />
        </Col>
        <Col xs={12} md={2} className="d-flex align-items-center">
          <div style={{ flex: 1 }}>
            <InputField
              label="Sub Category"
              name="product_name"
              value={formData.product_name || ""}
              onChange={handleChange}
              type="select"
              options={subcategoryOptions}
              disabled={vendorAssigned || isTagEntryProduct}
            />
          </div>
          <AiOutlinePlus
            size={20}
            color="black"
            style={{
              marginLeft: "10px",
              cursor: isTagEntryProduct ? "not-allowed" : "pointer",
              marginBottom: "20px",
              ...(isTagEntryProduct && { opacity: 0.5 }),
            }}
            // onClick={() => navigate("/subcategory", { state: { from: "/sales" } })}
            onClick={() =>
              !isTagEntryProduct && navigate("/subcategoryv2", {
                state: { orderDetails: [formData], from: "/orders" },
              })
            }
          />
        </Col>
        <Col xs={12} md={2}>
          <InputField
            label="Product Design Name"
            name="design_name"
            value={formData.design_name}
            onChange={handleChange}
            type="select"
            options={designOptions}
            disabled={vendorAssigned || isTagEntryProduct}
          />
        </Col>

        <Col xs={12} md={2}>
          <InputField
            label="Pricing"
            name="pricing"
            type="select"
            value={formData.pricing || ""} // Default to "Gross Weight"
            onChange={handleChange}
            options={[
              { value: "By Weight", label: "By Weight" },
              { value: "By fixed", label: "By fixed" },
              ...(formData.pricing &&
              !["By Weight", "By fixed"].includes(formData.pricing)
                ? [{ value: formData.pricing, label: formData.pricing }]
                : []),
            ]}
            disabled={vendorAssigned || isTagEntryProduct}
          />
        </Col>

        {/* Render different fields based on Pricing selection */}
        {isByFixed ? (
          // If Pricing is "By fixed", show only these fields:
          <>
            <Col xs={12} md={2}>
              <InputField
                label="Piece Cost"
                name="pieace_cost"
                value={formData.pieace_cost}
                onChange={handleChange}
                disabled={vendorAssigned || isTagEntryProduct}
              />
            </Col>
            <Col xs={12} md={1}>
              <InputField
                label="Qty"
                name="qty"
                value={formData.qty}
                onChange={handleChange}
                readOnly={!isQtyEditable}
                disabled={vendorAssigned || isTagEntryProduct}
              />
            </Col>
            <Col xs={12} md={2}>
              <InputField
                label="Taxable Amt"
                name="piece_taxable_amt"
                value={formData.piece_taxable_amt || "0.00"}
                onChange={handleChange}
                readOnly
                disabled={vendorAssigned || isTagEntryProduct}
              />
            </Col>
            <Col xs={12} md={1}>
              <InputField
                label="Tax%"
                name="tax_percent"
                value={formData.tax_percent}
                onChange={handleChange}
                disabled={vendorAssigned || isTagEntryProduct}
              />
            </Col>
            <Col xs={12} md={1}>
              <InputField
                label="Tax Amt"
                name="tax_amt"
                value={formData.tax_amt || "0.00"} // Default to "0.00" if undefined
                onChange={handleChange} // Optional, since it's auto-calculated
                readOnly
                disabled={vendorAssigned || isTagEntryProduct}
              />
            </Col>
            <Col xs={12} md={1}>
              <InputField
                label="MRP"
                name="mrp_price"
                value={formData.mrp_price || "0.00"}
                onChange={handleChange}
                readOnly={false}
                disabled={vendorAssigned || isTagEntryProduct}
              />
            </Col>
            <Col xs={12} md={2}>
              <InputField
                label="Total Price"
                name="total_price"
                value={formData.total_price || "0.00"} // Default to "0.00" if undefined
                onChange={handleChange} // Optional, since it's auto-calculated
                readOnly
                disabled={vendorAssigned || isTagEntryProduct}
              />
            </Col>
            <Col xs={12} md={2}>
              <InputField
                label="Remarks"
                name="remarks"
                value={formData.remarks}
                onChange={handleChange}
                disabled={vendorAssigned || isTagEntryProduct}
              />
            </Col>
            <Col xs={12} md={2}>
              <InputField
                label="Visibility"
                name="client_visibility"
                type="select"
                value={formData.client_visibility || "Internal Facing"}
                onChange={handleChange}
                options={[
                  { value: "Client Facing", label: "Client Facing" },
                  { value: "Internal Facing", label: "Internal Facing" },
                ]}
                disabled={vendorAssigned || isTagEntryProduct}
              />
            </Col>
            <Col xs={12} md={2}>
              <InputField
                label="Status"
                type="select"
                name="order_status"
                value={formData.order_status || "In Progress"}
                onChange={handleChange}
                options={[
                  { value: "Delivered", label: "Delivered" },
                  { value: "Not Delivered", label: "Not Delivered" },
                  { value: "In Progress", label: "In Progress" },
                ]}
                disabled={vendorAssigned || isTagEntryProduct}
              />
            </Col>
            {/* <Col xs={12} md={2}>
              <InputField
                label="Upload Image"
                name="image"
                type="file"
                onChange={handleImageChange}
                accept="image/*"
                ref={fileInputRef} // Attach ref to input field
              />
              {formData.imagePreview && (
                <div style={{ position: "relative", display: "inline-block" }}>
                  <img
                    src={formData.imagePreview}
                    alt="Selected"
                    style={{
                      width: "100px",
                      height: "100px",
                      marginTop: "10px",
                      borderRadius: "8px", // Optional: add rounded corners to the image
                    }}
                  />
                  <button
                    type="button"
                    onClick={clearImage}
                    disabled={isTagEntryProduct}
                    style={{
                      position: "absolute",
                      top: "5px",
                      right: "5px",
                      background: "transparent",
                      border: "none",
                      color: "red",
                      fontSize: "16px",
                      cursor: isTagEntryProduct ? "not-allowed" : "pointer",
                      opacity: isTagEntryProduct ? 0.5 : 1,
                      zIndex: 10,
                    }}
                  >
                    <FaTrash />
                  </button>
                </div>
              )}
            </Col> */}
            <Col xs={12} md={2}>
              <DropdownButton
                id="dropdown-basic-button"
                title="Choose / Capture Image"
                variant="primary"
                size="sm"
                disabled={isTagEntryProduct}
                onClick={() => !isTagEntryProduct && setShowOptions(!showOptions)}
              >
                {showOptions && (
                  <>
                    <Dropdown.Item
                      onClick={() =>
                        !isTagEntryProduct && fileInputRef.current && fileInputRef.current.click()
                      }
                      disabled={isTagEntryProduct}
                    >
                      <FaUpload /> Choose Image
                    </Dropdown.Item>
                    <Dropdown.Item 
                      onClick={() => !isTagEntryProduct && setShowWebcam(true)}
                      disabled={isTagEntryProduct}
                    >
                      <FaCamera /> Capture Image
                    </Dropdown.Item>
                  </>
                )}
              </DropdownButton>

              {/* Hidden File Input */}
              <input
                type="file"
                name="image"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleImageChange}
                disabled={isTagEntryProduct}
              />

              {/* Webcam Section */}
              {showWebcam && (
                <div>
                  <Webcam
                    audio={false}
                    ref={webcamRef}
                    screenshotFormat="image/jpeg"
                    width={150}
                    height={150}
                  />
                  <Button
                    variant="success"
                    size="sm"
                    onClick={captureImage}
                    disabled={isTagEntryProduct}
                    style={{ marginRight: "5px" }}
                  >
                    Capture
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowWebcam(false)}
                    disabled={isTagEntryProduct}
                  >
                    Cancel
                  </Button>
                </div>
              )}
              {/* Image Preview */}
              {formData.imagePreview && (
                <div
                  style={{
                    position: "relative",
                    display: "inline-block",
                    marginTop: "10px",
                  }}
                >
                  <img
                    src={formData.imagePreview}
                    alt="Selected"
                    style={{
                      width: "100px",
                      height: "100px",
                      borderRadius: "8px",
                    }}
                  />
                  <button
                    type="button"
                    onClick={clearImage}
                    disabled={vendorAssigned || isTagEntryProduct}
                    style={{
                      position: "absolute",
                      top: "5px",
                      right: "5px",
                      background: "transparent",
                      border: "none",
                      color: "red",
                      fontSize: "16px",
                      cursor: "pointer",
                      zIndex: 10,
                    }}
                  >
                    <FaTrash />
                  </button>
                </div>
              )}
            </Col>
          </>
        ) : (
          // Otherwise (if Pricing is "By Weight" or "By Running Tag"), show all fields:
          <>
            {/* <Col xs={12} md={2}>
              <InputField
                label="Purity"
                name="purity"
                value={formData.purity || ""}
                onChange={handleChange}
                type="select"
                options={purityOptions}
              />
            </Col> */}
            <Col xs={12} md={2}>
              {isRunningTag ? (
                <InputField
                  label="Selling Purity"
                  name="selling_purity"
                  value={
                    (() => {
                      const calculatedPurity = calculateSellingPurityForRunningTag();
                      const rateValue = formData.rate || "";
                      
                      if (calculatedPurity && rateValue) {
                        return `${calculatedPurity} (${rateValue})`;
                      } else if (calculatedPurity) {
                        return calculatedPurity;
                      }
                      return formData.custom_purity || formData.selling_purity || "";
                    })()
                  }
                  onChange={handleChange}
                  readOnly
                  disabled={vendorAssigned || isTagEntryProduct}
                />
              ) : (
                <InputField
                  label="Selling Purity"
                  name="selling_purity"
                  type="select"
                  value={formData.selling_purity}
                  onChange={handleChange}
                  disabled={vendorAssigned || isTagEntryProduct}
                  options={[
                    // { label: "Select Purity", value: "" },
                    ...(formData.product_name
                      ? [
                          {
                            label:
                              subcategoryOptions.find(
                                (option) => option.value === formData.product_name
                              )?.selling_purity || "Default Purity",
                            value:
                              subcategoryOptions.find(
                                (option) => option.value === formData.product_name
                              )?.selling_purity || "",
                          },
                        ]
                      : []),
                    { label: "Manual", value: "Manual" },
                  ]}
                />
              )}
            </Col>

            {!isRunningTag && formData.selling_purity === "Manual" && (
              <Col xs={12} md={2}>
                <InputField
                  label="Custom Purity %"
                  name="custom_purity"
                  value={formData.custom_purity || ""}
                  onChange={handleChange}
                  disabled={vendorAssigned || isTagEntryProduct}
                />
              </Col>
            )}
            <Col xs={12} md={1}>
              <InputField
                label="Gross Wt"
                name="gross_weight"
                value={formData.gross_weight || ""}
                onChange={handleChange}
                disabled={vendorAssigned || isTagEntryProduct}
              />
            </Col>
            <Col xs={12} md={1}>
              <InputField
                label="Stone Wt"
                name="stone_weight"
                value={formData.stone_weight || ""}
                onChange={handleChange}
                disabled={vendorAssigned || isTagEntryProduct}
              />
            </Col>
            <Col xs={12} md={1}>
              <InputField
                label="St Price"
                name="stone_price"
                value={formData.stone_price || ""}
                onChange={handleChange}
                disabled={vendorAssigned || isTagEntryProduct}
              />
            </Col>
            <Col xs={12} md={1}>
              <InputField
                label="Weight BW"
                name="weight_bw"
                value={formData.weight_bw || ""}
                onChange={handleChange}
                readOnly
                disabled={vendorAssigned || isTagEntryProduct}
              />
            </Col>

            <Col xs={12} md={2}>
              <InputField
                label="Wastage On"
                name="va_on"
                type="select"
                value={formData.va_on || ""} // Default to "Gross Weight"
                onChange={handleChange}
                options={[
                  { value: "Gross Weight", label: "Gross Weight" },
                  { value: "Weight BW", label: "Weight BW" },
                  ...(formData.va_on &&
                  !["Gross Weight", "Weight BW"].includes(formData.va_on)
                    ? [{ value: formData.va_on, label: formData.va_on }]
                    : []),
                ]}
                disabled={vendorAssigned || isTagEntryProduct}
              />
            </Col>
            <Col xs={12} md={1}>
              <InputField
                label="Wastage%"
                name="va_percent"
                value={formData.va_percent || "0"}
                onChange={handleChange}
                disabled={vendorAssigned || isTagEntryProduct}
              />
            </Col>
            <Col xs={12} md={1}>
              <InputField
                label="W.Wt"
                name="wastage_weight"
                value={formData.wastage_weight || "0.00"}
                onChange={handleChange}
                readOnly
                disabled={vendorAssigned || isTagEntryProduct}
              />
            </Col>
            <Col xs={12} md={2}>
              <InputField
                label="Total Weight AW"
                name="total_weight_av"
                value={formData.total_weight_av || ""}
                onChange={handleChange}
                readOnly
                disabled={vendorAssigned || isTagEntryProduct}
              />
            </Col>
            <Col xs={12} md={1}>
              <InputField
                label="Size"
                name="size"
                value={formData.size}
                onChange={handleChange}
                disabled={vendorAssigned || isTagEntryProduct}
              />
            </Col>

            {/* <Col xs={12} md={1}>
              <InputField
                label="Rate"
                name="rate"
                type="number"
                value={formData.rate}
                onChange={handleChange}
              />
            </Col> */}
            <Col xs={12} md={2}>
              <InputField
                label="Rate Type"
                name="rate_type"
                type="select"
                value={formData.rate_type || "Fixed"} 
                onChange={handleChange}
                options={[
                  { value: "Flexible", label: "Flexible" },
                  { value: "Fixed", label: "Fixed" },
                ]}
                disabled={isTagEntryProduct}
              />
            </Col>

            <Col xs={12} md={1}>
              <InputField
                label="Rate"
                name="rate"
                type="number"
                value={formData.rate}
                onChange={handleChange}
                disabled={formData.rate_type === "Fixed" || isTagEntryProduct} 
              />
            </Col>
            <Col xs={12} md={1}>
              <InputField
                label="Amount"
                name="rate_amt"
                value={formData.rate_amt || "0.00"} // Default to "0.00" if undefined
                onChange={handleChange} // Trigger recalculation of Total MC
                readOnly={false} // Ensure it's editable
                disabled={vendorAssigned || isTagEntryProduct}
              />
            </Col>
            <Col xs={12} md={2}>
              <InputField
                label="MC On"
                name="mc_on"
                type="select"
                value={formData.mc_on || ""}
                onChange={handleChange}
                options={[
                  { value: "MC / Gram", label: "MC / Gram" },
                  { value: "MC / Piece", label: "MC / Piece" },
                  { value: "MC %", label: "MC %" },
                  ...(formData.mc_on &&
                  !["MC / Gram", "MC / Piece", "MC %"].includes(formData.mc_on)
                    ? [{ value: formData.mc_on, label: formData.mc_on }]
                    : []),
                ]}
                disabled={vendorAssigned || isTagEntryProduct}
              />
            </Col>

            <Col xs={12} md={1}>
              <InputField
                label={formData.mc_on === "MC %" ? "MC %" : "MC/Gm"}
                name="mc_per_gram"
                value={formData.mc_per_gram || ""}
                onChange={handleChange}
                disabled={formData.mc_on === "MC / Piece" || vendorAssigned || isTagEntryProduct} // Disable when MC / Piece is selected
              />
            </Col>

            <Col xs={12} md={1}>
              <InputField
                label="Total MC"
                name="making_charges"
                value={formData.making_charges || ""}
                onChange={handleChange}
                disabled={formData.mc_on === "MC / Gram" || vendorAssigned || isTagEntryProduct} // Disable when MC / Gram is selected
              />
            </Col>

            {/* <Col xs={12} md={2}>
              <InputField
                label="Disscount %"
                name="disscount_percentage"
                value={formData.disscount_percentage || ""} // Display calculated Total MC
                onChange={handleChange}
              />
            </Col>

            <Col xs={12} md={2}>
              <InputField
                label="Total Disscount"
                name="disscount"
                value={formData.disscount || ""} // Display calculated Total MC
                onChange={handleChange}
              />
            </Col> */}
            {/* <Col xs={12} md={1}>
              <InputField
                label="Qty"
                name="qty"
                value={formData.qty}
                onChange={handleChange}
                readOnly={!isQtyEditable}
              />
            </Col> */}
            <Col xs={12} md={1}>
              <InputField
                label="HM Charge"
                name="hm_charges"
                value={formData.hm_charges || "0.00"} // Default to "0.00" if undefined
                onChange={handleChange} // Optional, since it's auto-calculated
                disabled={vendorAssigned || isTagEntryProduct}
              />
            </Col>
            <Col xs={12} md={1}>
              <InputField
                label="Tax%"
                name="tax_percent"
                value={formData.tax_percent}
                onChange={handleChange}
                disabled={vendorAssigned || isTagEntryProduct}
              />
            </Col>
            <Col xs={12} md={1}>
              <InputField
                label="Tax Amt"
                name="tax_amt"
                value={formData.tax_amt || "0.00"} // Default to "0.00" if undefined
                onChange={handleChange} // Optional, since it's auto-calculated
                readOnly
                disabled={vendorAssigned || isTagEntryProduct}
              />
            </Col>
            <Col xs={12} md={2}>
              <InputField
                label="Total Price"
                name="total_price"
                value={formData.total_price || "0.00"} // Default to "0.00" if undefined
                onChange={handleChange} // Optional, since it's auto-calculated
                readOnly
                disabled={vendorAssigned || isTagEntryProduct}
              />
            </Col>
            <Col xs={12} md={2}>
              <InputField
                label="Remarks Type"
                name="remarks_type"
                type="select"
                value={formData.remarks_type}
                onChange={handleChange}
                options={[
                  { value: "Client Facing", label: "Client Facing" },
                  { value: "Internal Facing", label: "Internal Facing" },
                ]}
                disabled={isTagEntryProduct}
              />
            </Col>
            <Col xs={12} md={2}>
              <InputField
                label="Remarks"
                name="remarks"
                value={formData.remarks}
                onChange={handleChange}
                disabled={isTagEntryProduct}
              />
            </Col>
            <Col xs={12} md={2} style={{
                  display: "none",
                }}>
              <InputField
                label="Reservation Status"
                type="select"
                name="reservation_status"
                value={formData.reservation_status}
                onChange={handleChange}
                options={[
                  { value: "Reserved", label: "Reserved" },
                  { value: "Not Reserved", label: "Not Reserved" },
                ]}
                disabled={vendorAssigned || isTagEntryProduct}
              />
            </Col>
            <Col xs={12} md={2}>
              <InputField
                label="Delivery Status"
                type="select"
                name="order_status"
                value={formData.order_status}
                onChange={handleChange}
                options={[
                  { value: "Delivered", label: "Delivered" },
                  { value: "Not Delivered", label: "Not Delivered" },
                ]}
                disabled={vendorAssigned || isTagEntryProduct}
              />
            </Col>
            {/* <Col xs={12} md={2}>
              <InputField
                label="Upload Image"
                name="image"
                type="file"
                onChange={handleImageChange}
                accept="image/*"
                ref={fileInputRef} // Attach ref to input field
              />
              {formData.imagePreview && (
                <div style={{ position: "relative", display: "inline-block" }}>
                  <img
                    src={formData.imagePreview}
                    alt="Selected"
                    style={{
                      width: "100px",
                      height: "100px",
                      marginTop: "10px",
                      borderRadius: "8px", // Optional: add rounded corners to the image
                    }}
                  />
                  <button
                    type="button"
                    onClick={clearImage}
                    disabled={isTagEntryProduct}
                    style={{
                      position: "absolute",
                      top: "5px",
                      right: "5px",
                      background: "transparent",
                      border: "none",
                      color: "red",
                      fontSize: "16px",
                      cursor: isTagEntryProduct ? "not-allowed" : "pointer",
                      opacity: isTagEntryProduct ? 0.5 : 1,
                      zIndex: 10,
                    }}
                  >
                    <FaTrash />
                  </button>
                </div>
              )}
            </Col> */}
            <Col xs={12} md={2}>
              <DropdownButton
                id="dropdown-basic-button"
                title="Choose / Capture Image"
                variant="primary"
                size="sm"
                disabled={isTagEntryProduct}
                onClick={() => !isTagEntryProduct && setShowOptions(!showOptions)}
              >
                {showOptions && (
                  <>
                    <Dropdown.Item
                      onClick={() =>
                        !isTagEntryProduct && fileInputRef.current && fileInputRef.current.click()
                      }
                      disabled={isTagEntryProduct}
                    >
                      <FaUpload /> Choose Image
                    </Dropdown.Item>
                    <Dropdown.Item 
                      onClick={() => !isTagEntryProduct && setShowWebcam(true)}
                      disabled={isTagEntryProduct}
                    >
                      <FaCamera /> Capture Image
                    </Dropdown.Item>
                  </>
                )}
              </DropdownButton>

              {/* Hidden File Input */}
              <input
                type="file"
                name="image"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleImageChange}
                disabled={isTagEntryProduct}
              />

              {/* Webcam Section */}
              {showWebcam && (
                <div>
                  <Webcam
                    audio={false}
                    ref={webcamRef}
                    screenshotFormat="image/jpeg"
                    width={150}
                    height={150}
                  />
                  <Button
                    variant="success"
                    size="sm"
                    onClick={captureImage}
                    disabled={isTagEntryProduct}
                    style={{ marginRight: "5px" }}
                  >
                    Capture
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowWebcam(false)}
                    disabled={isTagEntryProduct}
                  >
                    Cancel
                  </Button>
                </div>
              )}
              {/* Image Preview */}
              {formData.imagePreview && (
                <div
                  style={{
                    position: "relative",
                    display: "inline-block",
                    marginTop: "10px",
                  }}
                >
                  <img
                    src={formData.imagePreview}
                    alt="Selected"
                    style={{
                      width: "100px",
                      height: "100px",
                      borderRadius: "8px",
                    }}
                  />
                  <button
                    type="button"
                    onClick={clearImage}
                    disabled={isTagEntryProduct}
                    style={{
                      position: "absolute",
                      top: "5px",
                      right: "5px",
                      background: "transparent",
                      border: "none",
                      color: "red",
                      fontSize: "16px",
                      cursor: isTagEntryProduct ? "not-allowed" : "pointer",
                      opacity: isTagEntryProduct ? 0.5 : 1,
                      zIndex: 10,
                    }}
                  >
                    <FaTrash />
                  </button>
                </div>
              )}
            </Col>
          </>
        )}

        <Col xs={12} md={1}>
         <Button
          onClick={isEditing ? handleUpdate : handleAdd}
          disabled={(!isEditing && vendorAssigned) || isTagEntryProduct}
          style={{
            backgroundColor: "#a36e29",
            borderColor: "#a36e29",
            padding: "4px 7px",
            marginTop: "3px",
            fontSize: "13px",
            marginLeft: "2rem",
            opacity: !isEditing && vendorAssigned ? 0.6 : 1,
            cursor: !isEditing && vendorAssigned ? "not-allowed" : "pointer",
          }}
        >
          {isEditing ? "Update" : "Add"}
        </Button>
        </Col>
        <Col xs={12} md={1}>
          <Button
            onClick={handleClear}
            disabled={isTagEntryProduct}
            style={{
              backgroundColor: "gray",
              borderColor: "gray",
              marginLeft: '10px',
              padding: "4px 7px",
              fontSize: "13px",
              marginTop: "3px"
            }}
          >
            Clear
          </Button>
        </Col>
        <Col>
          <Button
            onClick={handleGenerateOrder}
            style={{
              display: "none",
              backgroundColor: "#a36e29",
              borderColor: "#a36e29",
              padding: "4px 7px",
              marginTop: "3px",
              marginLeft: "-1px",
              fontSize: "13px",
            }}
          >
            Generate Order
          </Button>
        </Col>
      </Row>
    </Col>
  );
};

export default ProductDetails;
